import { useCallback, useMemo, useState } from "react";

export const useSortAndSearch = <T>(
    items: T[],
    getSearchText: (item: T) => string,
    getSortText: (item: T) => string,
    initialSortOrder: "asc" | "desc" = "asc"
) => {
    const [searchKeyword, setSearchKeyword] = useState("");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">(initialSortOrder);

    const sortItems = useCallback(
        (list: T[], order: "asc" | "desc") =>
            [...list].sort((a, b) => {
                const comparison = getSortText(a).localeCompare(getSortText(b), "ko");
                return order === "asc" ? comparison : -comparison;
            }),
        [getSortText]
    );

    const [sortedItems, setSortedItems] = useState<T[]>(() => sortItems(items, initialSortOrder));

    const setItems = useCallback(
        (list: T[]) => {
            setSortedItems(sortItems(list, sortOrder));
        },
        [sortItems, sortOrder]
    );

    const toggleSortOrder = useCallback(() => {
        setSortOrder((prev) => {
            const newOrder = prev === "asc" ? "desc" : "asc";
            setSortedItems((current) => sortItems(current, newOrder));
            return newOrder;
        });
    }, [sortItems]);

    const handleSearch = useCallback((keyword: string) => {
        setSearchKeyword(keyword);
    }, []);

    const filteredItems = useMemo(
        () =>
            sortedItems.filter((item) =>
                getSearchText(item).toLowerCase().includes(searchKeyword.toLowerCase())
            ),
        [sortedItems, searchKeyword, getSearchText]
    );

    return {
        searchKeyword,
        sortOrder,
        handleSearch,
        toggleSortOrder,
        filteredItems,
        setItems,
    };
};
